import { useCallback, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import axios from "axios";

import toast from "components/ui/toast/Toast";
import { useProcessToneOfVoiceDocument } from "lib/hooks/api/ai_tone_of_voice/useProcessToneOfVoiceDocument";
import { useToneOfVoiceDocumentProcessingStatus } from "lib/hooks/api/ai_tone_of_voice/useToneOfVoiceDocumentProcessingStatus";
import { useCreateCustomerDocuments } from "lib/hooks/api/customer_documents/useCreateCustomerDocuments";
import { CustomerDocumentType, ToneOfVoiceProcessingStatus } from "lib/types/graphQLEnums";

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20MB

export type ToneOfVoiceProcessingResult = {
  rawValue: string;
  label: string;
};

export type ToneOfVoiceProcessingState =
  | { status: "idle" }
  | { status: "uploading" }
  | { status: "processing"; processingId: string; sourceDocumentId: string }
  | {
      status: "streaming";
      processingId: string;
      sourceDocumentId: string;
      streamedRawValue: string;
    }
  | { status: "error"; error: string }
  | { status: "success"; result: ToneOfVoiceProcessingResult; sourceDocumentId: string };

export function useToneOfVoiceDocumentProcessing(options?: {
  onSuccess?: (result: ToneOfVoiceProcessingResult, sourceDocumentId: string) => void;
}) {
  const [state, setState] = useState<ToneOfVoiceProcessingState>({ status: "idle" });

  // Stash the latest onSuccess callback in a ref so handleCompleted stays stable
  // across renders and always sees the freshest handler.
  const onSuccessRef = useRef(options?.onSuccess);
  onSuccessRef.current = options?.onSuccess;

  const { mutateAsync: createCustomerDocuments } = useCreateCustomerDocuments();
  const { mutateAsync: processToneOfVoiceDocument } = useProcessToneOfVoiceDocument();

  const handleStreaming = useCallback((rawValue: string) => {
    setState((prev) => {
      if (prev.status === "processing") {
        return {
          status: "streaming",
          processingId: prev.processingId,
          sourceDocumentId: prev.sourceDocumentId,
          streamedRawValue: rawValue,
        };
      }
      if (prev.status === "streaming") {
        return { ...prev, streamedRawValue: rawValue };
      }
      return prev;
    });
  }, []);

  const handleCompleted = useCallback((result: ToneOfVoiceProcessingResult) => {
    setState((prev) => {
      if (prev.status === "processing" || prev.status === "streaming") {
        onSuccessRef.current?.(result, prev.sourceDocumentId);
        return { status: "success", result, sourceDocumentId: prev.sourceDocumentId };
      }
      return prev;
    });
  }, []);

  const handleError = useCallback((error: string) => {
    setState((prev) => {
      if (prev.status === "processing" || prev.status === "streaming") {
        return { status: "error", error };
      }
      return prev;
    });
  }, []);

  const processingId =
    state.status === "processing" || state.status === "streaming" ? state.processingId : "";

  useToneOfVoiceDocumentProcessingStatus(processingId, {
    enabled: Boolean(processingId),
    onData(data, errors) {
      if (errors && errors.length > 0) {
        Sentry.captureException(errors);
        handleError("Document processing failed. Please try again.");
        return;
      }

      const { status, rawValue, label, error } = data.toneOfVoiceDocumentProcessingStatus;

      if (status === ToneOfVoiceProcessingStatus.Streaming && rawValue) {
        handleStreaming(rawValue);
      } else if (status === ToneOfVoiceProcessingStatus.Completed && rawValue && label) {
        handleCompleted({ rawValue, label });
      } else if (status === ToneOfVoiceProcessingStatus.Failed) {
        handleError(error ?? "Document processing failed");
      }
    },
  });

  const reset = useCallback(() => {
    setState({ status: "idle" });
  }, []);

  const startUpload = useCallback(
    async (file: File) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error("File must be 20MB or smaller");
        return;
      }

      setState({ status: "uploading" });

      try {
        const createResult = await createCustomerDocuments({
          request: {
            newDocuments: [
              {
                fileName: file.name.trim(),
                isFolder: false,
                documentType: CustomerDocumentType.ToneOfVoiceSource,
                metadata: {
                  fileSizeBytes: file.size,
                  mimeType: file.type,
                },
              },
            ],
          },
        });

        const doc = createResult.createCustomerDocuments.documents[0];
        if (!doc?.signedUrl) {
          throw new Error("No signed URL returned for document upload");
        }

        await axios.put(doc.signedUrl, file, {
          headers: { "Content-Type": "application/octet-stream" },
        });

        const processResult = await processToneOfVoiceDocument({
          input: { customerDocumentId: doc.id },
        });

        setState({
          status: "processing",
          processingId: processResult.processToneOfVoiceDocument.processingId,
          sourceDocumentId: doc.id,
        });
      } catch (error) {
        Sentry.captureException(error);
        toast.error("Failed to upload document");
        setState({ status: "error", error: "Upload failed" });
      }
    },
    [createCustomerDocuments, processToneOfVoiceDocument],
  );

  return { state, startUpload, reset };
}
